import "./customer-info-popup.css";
import * as ko from 'knockout';
import * as koMapping from 'knockout.mapping';

import {dxKnockoutPopupOptions, Popup} from '../../services/common/popup-helper';
import {dxKnockoutTextBoxOptions} from "../../services/common/common-types";
import {PopupParamsWithPlayload} from "../../services/popups/popup";
import {QboExportResultPopupParams} from "../qbo-export-result-popup/qbo-export-result-popup";
import {PopupType} from "../../services/popups/popupType";

type CustomerInfoViewModel = {
    id: KnockoutObservable<number>,
    firstName: KnockoutObservable<string>,
    middleInitial: KnockoutObservable<string>,
    lastName: KnockoutObservable<string>,
    company: KnockoutObservable<string>,
    streetAddress: KnockoutObservable<string >,
    streetAddress2: KnockoutObservable<string>,
    city: KnockoutObservable<string>,
    state: KnockoutObservable<string>,
    zip: KnockoutObservable<string>,
    phone: KnockoutObservable<string>,
    email: KnockoutObservable<string>,
}

export enum PopupMode {
    CustomerInfo,
    QboCustomer
}

export type CustomerInfoPopupParams = {
    mode: PopupMode;
    customerId?: string;
    customerName?: string;
}

class CustomerInfoPopupViewModel {

    private isLoading = ko.observable<boolean>(false);
    
    private popupMode = ko.observable<PopupMode>(PopupMode.CustomerInfo);
    
    private saveButtonVisible = ko.computed<boolean>(() => {
        return this.popupMode() == PopupMode.CustomerInfo;
    });

    private createQboCustomerButtonVisible = ko.computed<boolean>(() => {
        return this.popupMode() == PopupMode.QboCustomer;
    });

    popup = new Popup(<dxKnockoutPopupOptions>{
        title: "Customer Info",
        minWidth: 560,
        width: 560,
        minHeight: 550,
        height: 650,
        dragEnabled: true,
        shading: false,
        contentTemplate: "template",
        wrapper: ".customer-info-popup",
        onEnterKeyDown: () => { 
            if (this.saveButtonVisible()) {
                this.save(); } else {
                this.createQboCustomer();
            }
        },
        
        visible: this.params.visible,

        toolbarItems: [{
            widget: "dxButton",
            toolbar: "bottom",
            location: "before",
            options: {
                text: "Save",
                type: "default",
                visible: this.saveButtonVisible,
                onClick: () => {
                    let self = this;
                    self.save();
                }
            }
        }, {
            widget: "dxButton",
            toolbar: "bottom",
            location: "before",
            options: {
                text: "Create QuickBooks Customer",
                type: "default",
                visible: this.createQboCustomerButtonVisible,
                onClick: () => {
                    let self = this;
                    self.createQboCustomer();
                }
            }
        }, {
            widget: "dxButton",
            toolbar: "bottom",
            location: "before",
            options: {
                text: "Close",
                type: "default",
                onClick: () => {
                    this.params.visible(false);
                }
            }
        }, {
            html: "<a id=\"helpCustomerInfo\" class='context-help-link-header glyphicon glyphicon-info-sign'></a>",
            location: "before",
        }],

        onShown: (e) => {

            setTimeout(() => {
                if(!this.instances.firstName) {
                    return;
                }

                let element = this.instances.firstName.element();
                let input = element.getElementsByTagName("input");
                if(!input.length)
                    return;

                input[0].focus();
                input[0].select();
            }, 0);
        }
    });


    
    model : CustomerInfoViewModel = {
        firstName: ko.observable<string>("").extend({
            dxValidator: {
                validationRules: [{ type: 'required', message: 'Please enter the first name.' }]
            },
        }),
        id: ko.observable<number>(0),
        middleInitial: ko.observable<string>(""),
        lastName: ko.observable<string>("").extend({
            dxValidator: {
                validationRules: [{ type: 'required', message: 'Please enter the last name.' }]
            },
        }),
        company: ko.observable<string>(""),
        streetAddress: ko.observable<string>(""),
        streetAddress2: ko.observable<string>(""),
        city: ko.observable<string>(""),
        state: ko.observable<string>(""),
        zip: ko.observable<string>(""),
        phone: ko.observable<string>(""),
        email: ko.observable<string>("").extend({
            dxValidator: {
                validationRules: [{ type: 'email', message: 'Email is invalid.' }]
            },
        }),
    };
    
    constructor(private params: PopupParamsWithPlayload<CustomerInfoPopupParams>) {
        DevExpress.validationEngine.registerModelForValidation(this.model);
        
        this.params.visible.subscribe(visible => {
            if(!visible)
                return;

            DevExpress.validationEngine.resetGroup(this.model);
            let estimate = this.params.app.state.estimate();
            
            if(!estimate) {
                this.params.visible(false);
            }
            
            this.popupMode(this.params.data!.mode);
            
            this.popup.setTitle(this.popupMode() == PopupMode.CustomerInfo ? "Customer Info" : "Create QuickBooks Customer");
            
            this.params.app.apiManager.customersEstimates.load({ estimateId: estimate!.id() }).then(customer => {
                koMapping.fromJS(customer, {}, this.model);
            });
        });
    }

    instances = {
        firstName: <DevExpress.ui.dxTextBox | undefined> undefined
    };
    
    form = {
        firstName: <dxKnockoutTextBoxOptions> {
            placeholder: 'First Name',
            width: 325,
            value: this.model.firstName,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
            isValid: this.model.firstName.dxValidator!.isValid,
            validationError: this.model.firstName.dxValidator!.validationError,
            onInitialized: (e) => {
                this.instances.firstName = e.component;
            }
        },
        middleInitial: <dxKnockoutTextBoxOptions> {
            placeholder: 'Middle Initial',
            width: 325,
            value: this.model.middleInitial,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
        },
        lastName: <dxKnockoutTextBoxOptions> {
            placeholder: 'Last Name',
            width: 325,
            value: this.model.lastName,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
            isValid: this.model.lastName.dxValidator!.isValid,
            validationError: this.model.lastName.dxValidator!.validationError,
        },
        company: <dxKnockoutTextBoxOptions> {
            placeholder: 'Company',
            width: 325,
            value: this.model.company,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
        },
        streetAddress: <dxKnockoutTextBoxOptions> {
            placeholder: 'Street Address',
            width: 325,
            value: this.model.streetAddress,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
        },
        streetAddress2: <dxKnockoutTextBoxOptions> {
            placeholder: 'Second Address Line',
            width: 325,
            value: this.model.streetAddress2,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
        },
        city: <dxKnockoutTextBoxOptions> {
            placeholder: 'City',
            width: 325,
            value: this.model.city,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
        },
        state: <dxKnockoutTextBoxOptions> {
            placeholder: 'State',
            width: 325,
            value: this.model.state,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
        },
        zip: <dxKnockoutTextBoxOptions> {
            placeholder: 'Zip',
            width: 325,
            value: this.model.zip,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
        },
        phone: <dxKnockoutTextBoxOptions> {
            placeholder: 'Phone',
            width: 325,
            value: this.model.phone,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
        },
        email: <dxKnockoutTextBoxOptions> {
            placeholder: 'Email',
            width: 325,
            value: this.model.email,
            valueChangeEvent: 'keyup',
            disabled: this.isLoading,
            isValid: this.model.email.dxValidator!.isValid,
            validationError: this.model.email.dxValidator!.validationError,
        },
    };

    helpCustomerInfo = {
        target: "#helpCustomerInfo",
        showEvent: "dxclick",
        hideEvent: "dxhoverend",
        position: "bottom",
        showTitle: false,
        width: 320,
        visible: false,
        animation: {
            show: {
                type: "pop",
                from: {  scale: 0 },
                to: { scale: 1 }
            },
            hide: {
                type: "fade",
                from: 1,
                to: 0
            }
        }
    };
    
    save() {
        this.params.app.apiManager.customersEstimates.save({
            id: this.model.id(),
            estimateId: this.params.app.state.estimate()!.id(),
            firstName: this.model.firstName(),
            middleInitial: this.model.middleInitial(),
            lastName: this.model.lastName(),
            company: this.model.company(),
            streetAddress: this.model.streetAddress(),
            streetAddress2: this.model.streetAddress2(),
            city: this.model.city(),
            state: this.model.state(),
            zip: this.model.zip(),
            phone: this.model.phone(),
            email: this.model.email()
        }).then(customer => {
            this.params.app.state.customerInfo(new Date());
            this.params.visible(false);
        });
    }
    
    createQboCustomer() {
        let result = DevExpress.validationEngine.validateModel(this.model);
        if (!result.isValid)
            return;
        
        this.params.app.apiManager.qbo.createCustomer({
            id: this.model.id(),
            estimateId: this.params.app.state.estimate()!.id(),
            firstName: this.model.firstName(),
            middleInitial: this.model.middleInitial(),
            lastName: this.model.lastName(),
            company: this.model.company(),
            streetAddress: this.model.streetAddress(),
            streetAddress2: this.model.streetAddress2(),
            city: this.model.city(),
            state: this.model.state(),
            zip: this.model.zip(),
            phone: this.model.phone(),
            email: this.model.email()
        }).then((result)  => {
            if (result!.isSuccess) {
                this.params.data!.customerId = result!.customerId;
                this.params.data!.customerName = result!.customerName;
                this.params.reject = false;
                this.params.visible(false);
            } else {
                this.params.app.popupsManager.show<QboExportResultPopupParams>(PopupType.qboExportResult, true, {exportResult: result});
            }
        });
    }
}

export default {
    viewModel: CustomerInfoPopupViewModel,
    template: require('./customer-info-popup.html')
};


// WEBPACK FOOTER //
// ./ClientApp/components/customer-info-popup/customer-info-popup.ts